import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import { ArrowBack, CheckCircle, Info } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import PageHeader from '../components/common/PageHeader';

const QuoteAcceptanceDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [acceptanceDetails, setAcceptanceDetails] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAcceptanceDetails();
  }, [orderId]);

  const fetchAcceptanceDetails = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      // Fetch quote acceptance details
      const acceptanceResponse = await axios.get(
        `${config.apiUrl}/api/quote/acceptance-details/${orderId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (acceptanceResponse.data.success) {
        setAcceptanceDetails(acceptanceResponse.data.data);
      }

      // Fetch basic order details
      const orderResponse = await axios.get(
        `${config.apiUrl}/api/stationery-orders/${orderId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (orderResponse.data.success) {
        setOrderDetails(orderResponse.data.data);
      }

    } catch (err) {
      console.error('Error fetching acceptance details:', err);
      setError(err.response?.data?.message || 'Failed to load quote acceptance details');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkflow = () => {
    navigate('/app/order-workflow');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBack />}
          onClick={handleBackToWorkflow}
        >
          Back to Order Workflow
        </Button>
      </Container>
    );
  }

  if (!acceptanceDetails) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          No quote acceptance details found for this order.
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBack />}
          onClick={handleBackToWorkflow}
        >
          Back to Order Workflow
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <PageHeader 
        title="Quote Acceptance Details"
        subtitle={`Order #${orderId}`}
        action={
          <Button 
            variant="outlined" 
            startIcon={<ArrowBack />}
            onClick={handleBackToWorkflow}
          >
            Back to Workflow
          </Button>
        }
      />

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Order Information Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="h6" color="success.main">
                  Quote Accepted
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Order ID
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    #{orderId}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Acceptance Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(acceptanceDetails.acceptedAt)}
                  </Typography>
                </Grid>

                {orderDetails && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Organisation
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {orderDetails.organization || orderDetails.businessName || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Product Type
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {orderDetails.productType || 'N/A'}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Selected Option Details Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Info sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6">
                  Selected Option Details
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />

              {acceptanceDetails.selectedOption ? (
                // Multi-option quote - show selected option details
                <>
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Multi-Option Quote" 
                      color="primary" 
                      variant="outlined" 
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Selected Option
                      </Typography>
                      <Typography variant="h6" gutterBottom>
                        {acceptanceDetails.selectedOption.name}
                      </Typography>
                    </Grid>

                    {acceptanceDetails.selectedOption.description && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Description
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {acceptanceDetails.selectedOption.description}
                        </Typography>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Selected Price
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        ${acceptanceDetails.selectedOptionPrice?.toFixed(2) || '0.00'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Option ID
                      </Typography>
                      <Typography variant="body1">
                        #{acceptanceDetails.selectedOptionId}
                      </Typography>
                    </Grid>

                    {acceptanceDetails.selectedOption.details && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Option Specifications
                        </Typography>
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1 
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            style={{ whiteSpace: 'pre-wrap' }}
                          >
                            {acceptanceDetails.selectedOption.details}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </>
              ) : (
                // Single option quote
                <>
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Single Option Quote" 
                      color="info" 
                      variant="outlined" 
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body1" gutterBottom>
                        This was a single-option quote. The client accepted the only available option.
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Accepted Price
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        ${acceptanceDetails.selectedOptionPrice?.toFixed(2) || '0.00'}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              startIcon={<ArrowBack />}
              onClick={handleBackToWorkflow}
            >
              Back to Order Workflow
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default QuoteAcceptanceDetails;
